import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchJson } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import './Setup.css';

const TEXTS = {
  es: {
    welcome: '¡Bienvenido a ESA Logística!',
    almostReady: 'Estamos casi listos',
    storeNameTitle: 'Identificá tu tienda',
    storeNameDesc: 'Confirmá el nombre con el que vamos a registrar tu cuenta.',
    storeNameLabel: 'Nombre de la tienda',
    storeNameHint: 'Podés modificar el nombre sugerido por Tiendanube.',
    passwordTitle: 'Crea tu contraseña segura',
    passwordDesc: 'Asegúrate que sea fuerte y única',
    clientDataTitle: 'Completá los datos del cliente',
    clientDataDesc: 'Necesitamos estos datos para activar tu cuenta y calcular tus envíos.',
    emailLabel: 'Correo de acceso',
    emailHint: 'Usaremos este correo para iniciar sesión y recuperar tu contraseña. Podés cambiarlo.',
    emailPlaceholder: 'tu@correo.com',
    syntheticEmailWarning: 'Este correo fue generado automáticamente. Cambialo por un correo real para recibir la recuperación de contraseña.',
    passwordLabel: 'Contraseña',
    cuitLabel: 'CUIT',
    cuitPlaceholder: '20-12345678-3',
    addressLabel: 'Domicilio',
    addressPlaceholder: 'Calle y número',
    postalCodeLabel: 'Código postal',
    postalCodePlaceholder: '1234',
    invoiceTypeAuto: 'Factura A se configurará automáticamente.',
    passwordStrength: 'Fortaleza:',
    passwordWeak: 'Débil',
    passwordFair: 'Regular',
    passwordStrong: 'Fuerte',
    passwordVeryStrong: 'Muy fuerte',
    successTitle: '¡Listo!',
    successCreated: 'Cuenta creada',
    successLinked: 'Vinculada con Tiendanube',
    successReady: 'Estás listo para usar ESA Logística',
    resourcesPendingTitle: 'Medio de envío pendiente',
    resourcesPendingDescription: 'La cuenta se creó, pero Tiendanube todavía no confirmó el medio de envío. Soporte debe revisar la configuración antes de cotizar.',
    btnContinue: 'Continuar',
    btnGoToDashboard: 'Ir al Dashboard',
    businessNameLabel: 'Nombre de la cuenta',
    businessNameHint: 'Puedes editar el nombre sugerido por tu tienda.',
    loading: 'Cargando...',
    error: 'Error',
    errorTitle: 'No pudimos completar la configuración',
    errorHelp: 'Revisá el dato indicado y volvé a intentar.',
    setupErrors: {
      missing_setup_token: 'No recibimos un enlace de instalación válido. Volvé a instalar la aplicación desde Tiendanube.',
      invalid_setup_token: 'El enlace de instalación venció o ya fue utilizado. Desinstalá y reinstalá la aplicación para generar uno nuevo.',
      setup_info_failed: 'No pudimos obtener los datos de la tienda. Verificá la conexión e iniciá la instalación nuevamente.',
      missing_password: 'Ingresá una contraseña para continuar.',
      invalid_password: 'La contraseña debe tener al menos 8 caracteres.',
      missing_email: 'Ingresá un correo para crear la cuenta.',
      invalid_email: 'Ingresá un correo válido para crear la cuenta.',
      missing_cuit: 'Ingresá el CUIT de la empresa.',
      invalid_cuit: 'El CUIT debe contener 11 dígitos. El formato esperado es 20-12345678-3.',
      missing_address: 'Ingresá el domicilio fiscal de la empresa.',
      missing_postal_code: 'Ingresá el código postal del domicilio fiscal.',
      invoice_type: 'No pudimos configurar Factura A. Intentá nuevamente o contactá a soporte.',
      account_conflict: 'La tienda ya está asociada a otra cuenta. Contactá a soporte para revisar la vinculación.',
      setup_failed: 'No pudimos crear la cuenta en este momento. Intentá nuevamente en unos instantes.',
      connection_error: 'No pudimos comunicarnos con el servidor. Revisá tu conexión e intentá nuevamente.',
    },
    step1of3: 'Paso 1 de 3',
    step2of3: 'Paso 2 de 3',
    step3of3: 'Paso 3 de 3',
  },
  pt: {
    welcome: 'Bem-vindo à ESA Logística!',
    almostReady: 'Estamos quase prontos',
    storeNameTitle: 'Identifique sua loja',
    storeNameDesc: 'Confirme o nome com o qual vamos registrar sua conta.',
    storeNameLabel: 'Nome da loja',
    storeNameHint: 'Você pode alterar o nome sugerido pelo Tiendanube.',
    passwordTitle: 'Crie sua senha segura',
    passwordDesc: 'Certifique-se de que seja forte e única',
    clientDataTitle: 'Complete os dados do cliente',
    clientDataDesc: 'Precisamos destes dados para ativar sua conta e calcular seus envios.',
    emailLabel: 'E-mail de acesso',
    emailHint: 'Usaremos este e-mail para entrar e recuperar sua senha. Você pode alterá-lo.',
    emailPlaceholder: 'seu@email.com',
    syntheticEmailWarning: 'Este e-mail foi gerado automaticamente. Altere-o para um e-mail real para receber a recuperação da senha.',
    passwordLabel: 'Senha',
    cuitLabel: 'CUIT',
    cuitPlaceholder: '20-12345678-3',
    addressLabel: 'Endereço',
    addressPlaceholder: 'Rua e número',
    postalCodeLabel: 'Código postal',
    postalCodePlaceholder: '1234',
    invoiceTypeAuto: 'A Nota Fiscal A será configurada automaticamente.',
    passwordStrength: 'Força:',
    passwordWeak: 'Fraca',
    passwordFair: 'Regular',
    passwordStrong: 'Forte',
    passwordVeryStrong: 'Muito forte',
    successTitle: 'Pronto!',
    successCreated: 'Conta criada',
    successLinked: 'Vinculado ao Tiendanube',
    successReady: 'Você está pronto para usar ESA Logística',
    resourcesPendingTitle: 'Meio de envio pendente',
    resourcesPendingDescription: 'A conta foi criada, mas o Tiendanube ainda não confirmou o meio de envio. O suporte deve revisar a configuração antes de cotar.',
    btnContinue: 'Continuar',
    btnGoToDashboard: 'Ir ao Painel',
    businessNameLabel: 'Nome da conta',
    businessNameHint: 'Você pode editar o nome sugerido pela sua loja.',
    loading: 'Carregando...',
    error: 'Erro',
    errorTitle: 'Não foi possível concluir a configuração',
    errorHelp: 'Revise o dado indicado e tente novamente.',
    setupErrors: {
      missing_setup_token: 'Não recebemos um link de instalação válido. Instale o aplicativo novamente pelo Tiendanube.',
      invalid_setup_token: 'O link de instalação expirou ou já foi usado. Desinstale e instale o aplicativo novamente para gerar outro.',
      setup_info_failed: 'Não foi possível obter os dados da loja. Verifique a conexão e inicie a instalação novamente.',
      missing_password: 'Digite uma senha para continuar.',
      invalid_password: 'A senha deve ter pelo menos 8 caracteres.',
      missing_email: 'Digite um e-mail para criar a conta.',
      invalid_email: 'Digite um e-mail válido para criar a conta.',
      missing_cuit: 'Digite o CUIT da empresa.',
      invalid_cuit: 'O CUIT deve conter 11 dígitos. O formato esperado é 20-12345678-3.',
      missing_address: 'Digite o endereço fiscal da empresa.',
      missing_postal_code: 'Digite o código postal do endereço fiscal.',
      invoice_type: 'Não foi possível configurar a Nota Fiscal A. Tente novamente ou fale com o suporte.',
      account_conflict: 'A loja já está associada a outra conta. Fale com o suporte para revisar o vínculo.',
      setup_failed: 'Não foi possível criar a conta agora. Tente novamente em alguns instantes.',
      connection_error: 'Não foi possível comunicar com o servidor. Verifique a conexão e tente novamente.',
    },
    step1of3: 'Etapa 1 de 3',
    step2of3: 'Etapa 2 de 3',
    step3of3: 'Etapa 3 de 3',
  },
  en: {
    welcome: 'Welcome to ESA Logistics!',
    almostReady: 'We\'re almost ready',
    storeNameTitle: 'Identify your store',
    storeNameDesc: 'Confirm the name we will use to register your account.',
    storeNameLabel: 'Store name',
    storeNameHint: 'You can edit the name suggested by Tiendanube.',
    passwordTitle: 'Create your secure password',
    passwordDesc: 'Make sure it\'s strong and unique',
    clientDataTitle: 'Complete your client details',
    clientDataDesc: 'We need these details to activate your account and calculate shipping.',
    emailLabel: 'Login email',
    emailHint: 'We will use this email to sign in and recover your password. You can change it.',
    emailPlaceholder: 'you@email.com',
    syntheticEmailWarning: 'This email was generated automatically. Change it to a real email to receive password recovery messages.',
    passwordLabel: 'Password',
    cuitLabel: 'Tax ID (CUIT)',
    cuitPlaceholder: '20-12345678-3',
    addressLabel: 'Address',
    addressPlaceholder: 'Street and number',
    postalCodeLabel: 'Postal code',
    postalCodePlaceholder: '1234',
    invoiceTypeAuto: 'Invoice A will be configured automatically.',
    passwordStrength: 'Strength:',
    passwordWeak: 'Weak',
    passwordFair: 'Fair',
    passwordStrong: 'Strong',
    passwordVeryStrong: 'Very strong',
    successTitle: 'Done!',
    successCreated: 'Account created',
    successLinked: 'Linked with Tiendanube',
    successReady: 'You\'re ready to use ESA Logistics',
    resourcesPendingTitle: 'Shipping method pending',
    resourcesPendingDescription: 'The account was created, but Tiendanube has not confirmed the shipping method yet. Support must review the configuration before quoting.',
    btnContinue: 'Continue',
    btnGoToDashboard: 'Go to Dashboard',
    businessNameLabel: 'Account name',
    businessNameHint: 'You can edit the name suggested by your store.',
    loading: 'Loading...',
    error: 'Error',
    errorTitle: 'We could not complete the setup',
    errorHelp: 'Review the indicated value and try again.',
    setupErrors: {
      missing_setup_token: 'We did not receive a valid installation link. Install the application again from Tiendanube.',
      invalid_setup_token: 'The installation link expired or was already used. Uninstall and install the application again to generate a new one.',
      setup_info_failed: 'We could not retrieve the store data. Check the connection and start the installation again.',
      missing_password: 'Enter a password to continue.',
      invalid_password: 'The password must contain at least 8 characters.',
      missing_email: 'Enter an email to create the account.',
      invalid_email: 'Enter a valid email to create the account.',
      missing_cuit: 'Enter the company CUIT.',
      invalid_cuit: 'The CUIT must contain 11 digits. Expected format: 20-12345678-3.',
      missing_address: 'Enter the company tax address.',
      missing_postal_code: 'Enter the postal code for the tax address.',
      invoice_type: 'We could not configure Invoice A. Try again or contact support.',
      account_conflict: 'The store is already linked to another account. Contact support to review the link.',
      setup_failed: 'We could not create the account right now. Try again in a few moments.',
      connection_error: 'We could not communicate with the server. Check the connection and try again.',
    },
    step1of3: 'Step 1 of 3',
    step2of3: 'Step 2 of 3',
    step3of3: 'Step 3 of 3',
  },
};

// Keep the onboarding copy encoded in the bundle as valid native-language
// text even when the source is consumed by a legacy Windows toolchain.
const NATIVE_TEXT_OVERRIDES = {
  es: {
    welcome: '\u00a1Bienvenido a ESA Log\u00edstica!',
    passwordTitle: 'Crea tu contrase\u00f1a segura',
    passwordDesc: 'Aseg\u00farate de que sea fuerte y \u00fanica',
    passwordLabel: 'Contrase\u00f1a',
    passwordWeak: 'D\u00e9bil',
    successTitle: '\u00a1Listo!',
    successReady: 'Est\u00e1s listo para usar ESA Log\u00edstica',
  },
  pt: {
    welcome: 'Bem-vindo \u00e0 ESA Log\u00edstica!',
    passwordDesc: 'Certifique-se de que seja forte e \u00fanica',
    passwordStrength: 'For\u00e7a:',
    successReady: 'Voc\u00ea est\u00e1 pronto para usar ESA Log\u00edstica',
    businessNameHint: 'Voc\u00ea pode editar o nome sugerido pela sua loja.',
  },
};

Object.entries(NATIVE_TEXT_OVERRIDES).forEach(([locale, values]) => {
  Object.assign(TEXTS[locale], values);
});

Object.assign(TEXTS.es, {
  almostReady: 'Estamos casi listos',
  passwordStrength: 'Fortaleza:',
  passwordFair: 'Regular',
  passwordStrong: 'Fuerte',
  passwordVeryStrong: 'Muy fuerte',
  successCreated: 'Cuenta creada',
  successLinked: 'Vinculada con Tiendanube',
  businessNameHint: 'Puedes editar el nombre sugerido por tu tienda.',
  loading: 'Cargando...',
  error: 'Error',
  step1of3: 'Paso 1 de 3',
  step2of3: 'Paso 2 de 3',
  step3of3: 'Paso 3 de 3',
});

Object.assign(TEXTS.pt, {
  almostReady: 'Estamos quase prontos',
  passwordTitle: 'Crie sua senha segura',
  passwordLabel: 'Senha',
  passwordFair: 'Regular',
  passwordStrong: 'Forte',
  passwordVeryStrong: 'Muito forte',
  successTitle: 'Pronto!',
  successCreated: 'Conta criada',
  successLinked: 'Vinculada ao Tiendanube',
  businessNameLabel: 'Nome da conta',
  loading: 'Carregando...',
  error: 'Erro',
  step1of3: 'Etapa 1 de 3',
  step2of3: 'Etapa 2 de 3',
  step3of3: 'Etapa 3 de 3',
});

function getPasswordStrength(password) {
  if (!password) return 0;
  let strength = 0;
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[!@#$%^&*]/.test(password)) strength++;
  return strength;
}

function getStrengthLabel(strength, t) {
  if (strength <= 1) return t.passwordWeak;
  if (strength <= 2) return t.passwordFair;
  if (strength <= 3) return t.passwordStrong;
  return t.passwordVeryStrong;
}

const DEFAULT_TIPO_FACTURA_ID = 1;

function formatCuit(value) {
  const digits = String(value ?? '').replace(/\D/g, '').slice(0, 11);

  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

function isSyntheticTiendanubeEmail(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized.startsWith('user_') && normalized.endsWith('@tiendanube.com');
}

function isBlockingErrorCode(code) {
  return ['missing_setup_token', 'invalid_setup_token', 'setup_info_failed', 'account_conflict'].includes(code);
}

function createSetupError(code, t, detail = '') {
  return {
    code,
    message: t.setupErrors?.[code] || detail || t.setupErrors.setup_failed,
    blocking: isBlockingErrorCode(code),
  };
}

function getSetupError(error, t, fallbackCode = 'setup_failed') {
  const payload = error?.payload || {};
  const code = payload.errorCode || error?.errorCode || (error?.status === 401 ? 'invalid_setup_token' : fallbackCode);
  const detail = typeof error?.message === 'string' ? error.message : '';

  if (error?.status === 0 || detail.toLowerCase().includes('failed to fetch')) {
    return createSetupError('connection_error', t, detail);
  }

  return createSetupError(code, t, detail);
}

export default function Setup() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setupToken = searchParams.get('setupToken');
  const lang = (searchParams.get('lang') || 'es').toLowerCase();
  const t = TEXTS[lang] || TEXTS.es;

  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cuit, setCuit] = useState('');
  const [domicilio, setDomicilio] = useState('');
  const [codigoPostal, setCodigoPostal] = useState('');
  const [redirectTo, setRedirectTo] = useState('/portal');
  const [storeName, setStoreName] = useState('ESA Logística');
  const [resourcesSynced, setResourcesSynced] = useState(true);

  useEffect(() => {
    if (!setupToken) {
      setError(createSetupError('missing_setup_token', t));
    }
  }, [setupToken, t]);

  useEffect(() => {
    const initialStoreName = searchParams.get('storeName');
    if (initialStoreName) {
      setStoreName(initialStoreName);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!setupToken) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    fetchJson(`/auth/tiendanube/setup/info?setupToken=${encodeURIComponent(setupToken)}`)
      .then((data) => {
        if (cancelled) {
          return;
        }

        const store = data?.store || {};
        const client = data?.cliente || {};
        const storeEmail = String(store.email || store.contactEmail || '').trim();
        setEmail(storeEmail || (store.id ? `user_${store.id}@tiendanube.com` : ''));
        if (store.name) {
          setStoreName(store.name);
        }
        if (client.razonSocial) {
          setStoreName(client.razonSocial);
        }
        if (client.cuit && String(client.cuit) !== String(store.id || '')) {
          setCuit(formatCuit(client.cuit));
        }
        if (client.domicilio) {
          setDomicilio(client.domicilio);
        }
        if (client.codigoPostal) {
          setCodigoPostal(client.codigoPostal);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getSetupError(err, t, 'setup_info_failed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [setupToken, t]);

  const passwordStrength = getPasswordStrength(password);
  const strengthLabel = getStrengthLabel(passwordStrength, t);
  const strengthPercent = (passwordStrength / 5) * 100;
  const displayStoreName = storeName.includes('\u00c3') ? 'ESA Logistica' : storeName;

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const normalizedPassword = password.trim();
    const normalizedEmail = email.trim();
    const cuitDigits = cuit.replace(/\D/g, '');

    if (!normalizedPassword) {
      setError(createSetupError('missing_password', t));
      return;
    }

    if (normalizedPassword.length < 8) {
      setError(createSetupError('invalid_password', t));
      return;
    }

    if (!normalizedEmail) {
      setError(createSetupError('missing_email', t));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError(createSetupError('invalid_email', t));
      return;
    }

    if (!cuitDigits) {
      setError(createSetupError('missing_cuit', t));
      return;
    }

    if (cuitDigits.length !== 11) {
      setError(createSetupError('invalid_cuit', t));
      return;
    }

    if (!domicilio.trim()) {
      setError(createSetupError('missing_address', t));
      return;
    }

    if (!codigoPostal.trim()) {
      setError(createSetupError('missing_postal_code', t));
      return;
    }

    setLoading(true);
    try {
      const data = await fetchJson('/auth/tiendanube/setup/password', {
        method: 'POST',
        body: JSON.stringify({
          setupToken,
          newPassword: normalizedPassword,
          email: normalizedEmail,
          razonSocial: storeName.trim(),
          cuit: formatCuit(cuit),
          domicilio: domicilio.trim(),
          codigoPostal: codigoPostal.trim(),
          tipoFactura: DEFAULT_TIPO_FACTURA_ID,
        }),
      });

      if (!data.ok) {
        setError(getSetupError({ message: data.error, payload: data }, t));
        return;
      }

      setSession(data);

      setRedirectTo(data.redirectTo || '/portal');
      setResourcesSynced(data.resourcesSynced !== false);
      setStep(3);
    } catch (err) {
      setError(getSetupError(err, t));
    } finally {
      setLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate(redirectTo, { replace: true });
  };

  const showBlockingError = Boolean(error?.blocking);
  const errorMessage = error?.message || error;

  return (
    <div className="page-shell">
      <section className="setup-wizard" translate="no">
        <div className="setup-header">
          <div className="setup-logo">ESA</div>
          <div className="setup-progress" hidden={showBlockingError}>
            <span className="step-indicator">
              {[t.step1of3, t.step2of3, t.step3of3][step - 1]}
            </span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${(step / 3) * 100}%` }} />
            </div>
          </div>
        </div>

        <div className="setup-content">
          <section className="setup-error" hidden={!showBlockingError} aria-live="polite">
            <h1>{t.errorTitle}</h1>
            <p>{errorMessage}</p>
            <p>{t.errorHelp}</p>
            <button type="button" onClick={() => navigate('/login')} className="btn-primary">
              {t.btnContinue}
            </button>
          </section>

          <div
            className="alert error setup-inline-error"
            hidden={!error || showBlockingError || step === 3}
            role="alert"
          >
            <strong>{t.error}:</strong> {errorMessage} {t.errorHelp}
          </div>

          <div className="setup-steps" hidden={showBlockingError}>
            <section hidden={step !== 1} aria-hidden={step !== 1}>
              <h1>{t.storeNameTitle}</h1>
              <p className="setup-subtitle">{t.storeNameDesc}</p>
              <form
                className="setup-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  setError(null);
                  setStoreName((current) => current.trim());
                  setStep(2);
                }}
              >
                <div className="setup-info">
                  <label>
                    {t.storeNameLabel}
                    <input
                      type="text"
                      value={storeName}
                      onChange={(event) => setStoreName(event.target.value)}
                      placeholder={t.storeNameLabel}
                      autoComplete="organization"
                      required
                    />
                    <small>{t.storeNameHint}</small>
                  </label>
                  <p>
                    Tienda Tiendanube: <strong>{displayStoreName}</strong>
                  </p>
                </div>
                <button type="submit" className="btn-primary btn-large" disabled={!storeName.trim()}>
                  {t.btnContinue}
                </button>
              </form>
            </section>

            <section hidden={step !== 2} aria-hidden={step !== 2}>
              <h1>{t.clientDataTitle}</h1>
              <p className="setup-subtitle">{t.clientDataDesc}</p>
              <form onSubmit={handlePasswordSubmit} className="setup-form" noValidate>
                <label>
                  {t.emailLabel}
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.emailPlaceholder}
                    autoComplete="email"
                    required
                  />
                  <small>{t.emailHint}</small>
                </label>
                {isSyntheticTiendanubeEmail(email) ? (
                  <div className="setup-email-warning" role="status">
                    {t.syntheticEmailWarning}
                  </div>
                ) : null}

                <label>
                  {t.cuitLabel}
                  <input
                    type="text"
                    value={cuit}
                    onChange={(e) => setCuit(formatCuit(e.target.value))}
                    placeholder={t.cuitPlaceholder}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={13}
                    pattern="[0-9]{2}-[0-9]{8}-[0-9]"
                  />
                </label>

                <label>
                  {t.addressLabel}
                  <input
                    type="text"
                    value={domicilio}
                    onChange={(e) => setDomicilio(e.target.value)}
                    placeholder={t.addressPlaceholder}
                    autoComplete="street-address"
                    required
                  />
                </label>

                <label>
                  {t.postalCodeLabel}
                  <input
                    type="text"
                    value={codigoPostal}
                    onChange={(e) => setCodigoPostal(e.target.value)}
                    placeholder={t.postalCodePlaceholder}
                    inputMode="numeric"
                    autoComplete="postal-code"
                    required
                  />
                </label>

                <p className="setup-form-note">{t.invoiceTypeAuto}</p>

                <label>
                  {t.passwordLabel}
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength="8"
                    autoComplete="new-password"
                  />
                </label>

                <div className="password-strength" hidden={!password}>
                    <div className="strength-label">
                      {t.passwordStrength} <strong>{strengthLabel}</strong>
                    </div>
                    <div className="strength-bar">
                      <div
                        className={`strength-fill strength-${passwordStrength}`}
                        style={{ width: `${strengthPercent}%` }}
                      />
                    </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary btn-large"
                  disabled={loading}
                >
                  {loading ? t.loading : t.btnContinue}
                </button>
              </form>
            </section>

            <section hidden={step !== 3} aria-hidden={step !== 3}>
              <h1>{t.successTitle}</h1>
              <p className="setup-subtitle">{t.successReady}</p>
              <div className="setup-resource-warning" hidden={resourcesSynced} role="status">
                <strong>{t.resourcesPendingTitle}</strong>
                <p>{t.resourcesPendingDescription}</p>
              </div>
              <div className="setup-success">
                <div className="success-item">
                  <span className="success-icon">✓</span>
                  <span>{t.successCreated}</span>
                </div>
                <div className="success-item">
                  <span className="success-icon">✓</span>
                  <span>{t.successLinked}</span>
                </div>
              </div>
              <button onClick={handleGoToDashboard} className="btn-primary btn-large">
                {t.btnGoToDashboard}
              </button>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
}
